/**
 * Collects the small create/rename dialogs used by stored Vault items.
 *
 * These interactions share validation and confirmation behavior but do not own
 * hierarchy or authorization. Keeping them presentational lets VaultView invoke
 * one service-backed mutation path for folders, notes, and names.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui'

interface NewItemDialogProps {
  kind: 'folder' | 'note'
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (name: string) => Promise<boolean>
}

export function NewItemDialog({ kind, open, onOpenChange, onCreate }: NewItemDialogProps) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      if (!await onCreate(name.trim())) return
      setName('')
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Vault {kind === 'note' ? 'Note' : 'folder'}</DialogTitle>
          <DialogDescription>
            {kind === 'note'
              ? 'Notes are private to you until you share them.'
              : 'Create it in the current folder.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`vault-new-${kind}`}>Name</Label>
          <Input
            id={`vault-new-${kind}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void submit() }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface RenameItemDialogProps {
  name: string | null
  onOpenChange: (open: boolean) => void
  onRename: (name: string) => Promise<boolean>
}

export function RenameItemDialog({ name: initialName, onOpenChange, onRename }: RenameItemDialogProps) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => setName(initialName ?? ''), [initialName])

  const submit = async () => {
    if (!name.trim() || name.trim() === initialName) return
    setSaving(true)
    try {
      if (!await onRename(name.trim())) return
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={initialName !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename item</DialogTitle>
          <DialogDescription>Choose a clear name for this item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="vault-rename">Name</Label>
          <Input
            id="vault-rename"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void submit() }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => void submit()}
            disabled={saving || !name.trim() || name.trim() === initialName}
          >
            {saving ? 'Renaming…' : 'Rename'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
